import { v4 as uuid } from "uuid";
import type { CartItem, CartItemOption, Product, ProductOption } from "~/types";
import { isValidCartItem } from "./cart";

// Pooling lets an incomplete deal in the cart absorb matching plain menu items.
// A plain item "fits" a deal option when that option lists the item's product as
// an allowed choice and the option still has capacity. An item's quantity is
// distributed across the deal's matching slots; whatever doesn't fit stays a
// standalone line — no errors, no feedback. Only the new LocationDeal system
// uses this (deal items carry a resolved product via dealToProduct).

function selectedQuantity(option: CartItemOption | undefined): number {
  if (!option) return 0;
  return option.choices.reduce((total, c) => total + c.quantity, 0);
}

// Fill toward max; fall back to min when max is unbounded so optional/unbounded
// options don't absorb items indefinitely.
function optionCapacity(option: ProductOption): number {
  return option.maxQuantity ?? option.minQuantity;
}

// Whether `item`'s configuration is allowed by `product` (a deal choice product
// built from the deal definition). A product with `options: undefined` isn't
// restricted by the deal, so any config fits. Otherwise every option and every
// selected choice the item carries must be listed in the deal's constraints —
// this rejects e.g. a drink whose size the deal doesn't offer.
function itemFitsProduct(item: CartItem, product: Product): boolean {
  if (!product.options) return true;

  for (const itemOption of item.options) {
    const productOption = product.options.find(
      (o) => o.id === itemOption.optionId,
    );
    if (!productOption) return false;

    // An empty allowed-choice list means the deal doesn't restrict this option's
    // choices, so any choice fits.
    if (productOption.choices.length === 0) continue;

    for (const choice of itemOption.choices) {
      const allowed = productOption.choices.find(
        (c) => c.product.id === choice.productId,
      );
      if (!allowed) return false;
      if (!itemFitsProduct(choice, allowed.product)) return false;
    }
  }

  return true;
}

// Returns a new deal cart item with `choice` added into the given option
// (creating the option entry if the deal doesn't have it yet).
function insertChoiceIntoDeal(
  dealItem: CartItem,
  optionId: string,
  choice: CartItem,
): CartItem {
  const hasOption = dealItem.options.some((o) => o.optionId === optionId);

  const options: CartItemOption[] = hasOption
    ? dealItem.options.map((o) =>
        o.optionId === optionId ? { ...o, choices: [...o.choices, choice] } : o,
      )
    : [...dealItem.options, { optionId, choices: [choice] }];

  return { ...dealItem, options };
}

// Distributes as many of `plainItem`'s units as fit across the deal's matching
// open slots (respecting per-option capacity and config constraints). Each slot
// receives a fresh choice with its share of the quantity. Returns the updated
// deal and how many units were placed (0 = nothing fit).
function placeUnitsIntoDeal(
  dealItem: CartItem,
  dealProduct: Product,
  plainItem: CartItem,
): { dealItem: CartItem; placed: number } {
  let remaining = plainItem.quantity;
  let updated = dealItem;

  for (const option of dealProduct.options ?? []) {
    if (remaining <= 0) break;

    const matchedChoice = option.choices.find(
      (c) => c.product.id === plainItem.productId,
    );
    if (!matchedChoice) continue;
    if (!itemFitsProduct(plainItem, matchedChoice.product)) continue;

    const cap = optionCapacity(option);
    if (cap <= 0) continue;

    const cartOption = updated.options.find((o) => o.optionId === option.id);
    const room = cap - selectedQuantity(cartOption);
    if (room <= 0) continue;

    const place = Math.min(remaining, room);
    updated = insertChoiceIntoDeal(updated, option.id, {
      ...plainItem,
      lineItemId: uuid(),
      quantity: place,
      pooledFromPlain: true,
    });
    remaining -= place;
  }

  return { dealItem: updated, placed: plainItem.quantity - remaining };
}

// Code-entry helper: fill a freshly built deal from the plain items already in
// the cart (oldest first). Deal items are left untouched. Whatever quantity
// isn't absorbed stays as a standalone line.
export function poolExistingItemsIntoDeal(
  items: CartItem[],
  dealItem: CartItem,
  dealProduct: Product,
): { remainingItems: CartItem[]; dealItem: CartItem } {
  let filledDeal = dealItem;
  const remainingItems: CartItem[] = [];

  for (const item of items) {
    if (item.isDeal) {
      remainingItems.push(item);
      continue;
    }

    const { dealItem: updated, placed } = placeUnitsIntoDeal(
      filledDeal,
      dealProduct,
      item,
    );
    filledDeal = updated;

    const remaining = item.quantity - placed;
    if (remaining > 0) {
      remainingItems.push({ ...item, quantity: remaining });
    }
  }

  return { remainingItems, dealItem: filledDeal };
}

// Magnet helper: when a plain item is being added, drop as many of its units as
// fit into the oldest INCOMPLETE deal with matching open slots, leaving any
// remainder as a standalone line. `resolveDealProduct` maps a deal cart item to
// its resolved product (options).
export function poolPlainItemIntoDeals(
  items: CartItem[],
  newItem: CartItem,
  resolveDealProduct: (dealItem: CartItem) => Product | undefined,
): { items: CartItem[]; pooled: boolean } {
  if (newItem.isDeal) return { items, pooled: false };

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item.isDeal) continue;

    const dealProduct = resolveDealProduct(item);
    if (!dealProduct) continue;

    // Only incomplete deals absorb items.
    if (isValidCartItem(item, dealProduct.options).valid) continue;

    const { dealItem: updatedDeal, placed } = placeUnitsIntoDeal(
      item,
      dealProduct,
      newItem,
    );
    if (placed <= 0) continue;

    const nextItems = items.map((it, idx) => (idx === i ? updatedDeal : it));
    const remaining = newItem.quantity - placed;
    if (remaining > 0) {
      nextItems.push({ ...newItem, quantity: remaining });
    }

    return { items: nextItems, pooled: true };
  }

  return { items, pooled: false };
}
